import dictionaryApi from '@/api/dictionary'
import customerApi from '@/api/customer'
import cityApi from '@/api/city'
import orderTypeApi from '@/api/order-type'
import counterPartyApi from '@/api/counter-party'
import systemSettingsApi from '@/api/system-setting'
import paymentMethodApi from '@/api/payment-method'
import { generateFormHandlerMutations } from '@/helpers/forms'

const state = {
  languages: [],
  applicationTypesList: [],
  rolesList: [],
  contactTypesList: [],
  contactsList: [],
  vacancyTypesList: [],
  applicationDescriptionTypesList: [],
  sexList: [],
  customerList: [],
  cityList: [],
  counterPartyTypeList: [],
  tableList: [],
  cashList: [],
  rk7OrderTypeList: [],
  orderCategoryList: [],
  currencies: [],
  orderTypesList: [],
  crmClientGroupsList: [],
  crmContactTypesList: [],
  rk7ReasonsList: [],
  vacancyApplicationStatusList: [],
  crmAccountTypesList: [],
  paymentMethodTypesList: [],
  paymentMethodsList: [],
  deliveryPaymentTypesList: [],
  transferKindsList: [],
  paymentAmountScopesList: [],
  branchesList: [],
  orderStatesList: [],
  customDishTypesList: [],
}

const getters = {
  defaultLanguage: state => {
    const language = state.languages.find(x => x.isDefault)

    return language ? language.code : ''
  },
  customDishTypes: state => state.customDishTypesList,
  customDishTypesDropDown: state => state.customDishTypesList.map(x => ({
    text: x.name,
    value: x.id,
  })),
  vacancyApplicationStatusTab: state => state.vacancyApplicationStatusList,
  branchesDropDown: state => state.branchesList.map(x => ({ text: x.name, value: x.id })),
  paymentMethodTypesDropDown: state => state.paymentMethodTypesList.map(x => ({
    text: x.name,
    value: x.id,
  })),
  deliveryPaymentTypesDropDown: state => state.deliveryPaymentTypesList.map(x => ({
    text: x.name,
    value: x.id,
  })),
  transferKindsDropDown: state => state.transferKindsList.map(x => ({
    text: x.name,
    value: x.id,
  })),
  paymentAmountScopesDropDown: state => state.paymentAmountScopesList.map(x => ({
    text: x.name,
    value: x.id,
  })),
  crmAccountTypesDropDown: state => state.crmAccountTypesList.map(x => ({
    text: x.name,
    value: x.id,
  })),
  rk7ReasonsDropDown: state => state.rk7ReasonsList.map(x => ({ text: x.name, value: x.id })),
  crmClientGroupsDropDown: state => state.crmClientGroupsList.map(x => ({
    text: x.name,
    value: x.id,
  })),
  crmContactTypesDropDown: state => state.crmContactTypesList.map(x => ({
    text: x.name,
    value: x.id,
  })),
  orderTypesDropDown: state => state.orderTypesList.map(x => ({ text: x.name, value: x.id })),
  rk7OrderTypeDropDown: state => state.rk7OrderTypeList.map(x => ({ text: x.name, value: x.code })),
  orderCategoryDropDown: state => state.orderCategoryList.map(x => ({
    text: x.name,
    value: x.code,
  })),
  currencyDropDown: state => state.currencies.map(x => ({ text: x.name, value: x.code })),
  currencyDropDownId: state => state.currencies.map(x => ({ text: x.name, value: x.id })),
  tableDropDown: state => state.tableList.map(x => ({ text: x.name, value: x.code })),
  cashDropDown: state => state.cashList.map(x => ({ text: x.name, value: x.code })),
  counterPartyTypeDropDown: state => state.counterPartyTypeList.map(x => ({
    text: x.name,
    value: x.id,
  })),
  customerDropdown: state => state.customerList.map(x => ({ text: x.name, value: x.id })),
  cityDropDown: state => state.cityList.map(x => ({ text: x.name, value: x.id })),
  applicationTypesDropdown: state => state.applicationTypesList.map(x => ({
    text: x.name,
    value: x.id,
  })),
  applicationDescriptionTypesDropdown: state => state.applicationDescriptionTypesList.map(x => ({
    text: x.name,
    value: x.id,
  })),
  rolesDropdown: state => state.rolesList.map(x => ({ text: x.name, value: x.id })),
  paymentMethodsDropdown: state => state.paymentMethodsList.map(x => ({ text: x.name, value: x.id })),
  contactTypesDropdown: state => state.contactTypesList.map(x => ({ text: x.name, value: x.id })),
  contactsDropdown: state => state.contactsList.map(x => ({ text: x.name, value: x.id })),
  vacancyTypesDropdown: state => state.vacancyTypesList.map(x => ({ text: x.name, value: x.id })),
  sexDropdown: state => state.sexList.map(x => ({ text: x.name, value: x.id })),
  orderStatesDropDown: state => state.orderStatesList.map(x => ({ text: x.name, value: x.id })),
}

const actions = {
  fetchLanguages({ commit, state }) {
    if (state.languages.length) return Promise.resolve()

    return dictionaryApi.getLanguages().then(({ data }) => commit('SET_LANGUAGES', data))
  },
  fetchApplicationTypes({ commit }) {
    dictionaryApi.getApplicationTypes()
      .then(({ data }) => commit('SET_APPLICATION_TYPES_LIST', data))
  },
  fetchOrderTypes({ commit }) {
    dictionaryApi.getOrderTypes().then(({ data }) => commit('SET_ORDER_TYPES_LIST', data))
  },
  fetchPaymentMethods({ commit }) {
    return dictionaryApi.getPaymentMethods().then(({ data }) => commit('SET_PAYMENT_METHODS_LIST', data))
  },
  fetchRoles({ commit }) {
    dictionaryApi.getRoles()
      .then(({ data }) => commit('SET_ROLES_LIST', data))
  },
  fetchContactTypes({ commit }) {
    dictionaryApi.getContactTypes()
      .then(({ data }) => commit('SET_CONTACT_TYPES_LIST', data))
  },
  fetchContacts({ commit }) {
    dictionaryApi.getContacts()
      .then(({ data }) => commit('SET_CONTACTS_LIST', data))
  },
  fetchApplicationDescriptionTypes({ commit }) {
    dictionaryApi.getApplicationDescriptionTypes()
      .then(({ data }) => commit('SET_APPLICATION_DESCRIPTION_TYPES_LIST', data))
  },
  fetchVacancyTypes({ commit }) {
    dictionaryApi.getVacancyTypes()
      .then(({ data }) => commit('SET_VACANCY_TYPES_LIST', data))
  },
  fetchSex({ commit }) {
    dictionaryApi.getSex()
      .then(({ data }) => commit('SET_SEX_LIST', data))
  },
  fetchCustomersDropdown({ commit }, payload) {
    return customerApi.getCustomersDropdown(payload)
      .then(({ data }) => commit('SET_CUSTOMER_DROPDOWN', data))
  },
  fetchCities({ commit }) {
    return cityApi.getDropDown()
      .then(({ data }) => commit('SET_CITY_DROPDOWN', data))
  },
  fetchCounterPartyTypes({ commit }) {
    dictionaryApi.getCounterPartyTypes()
      .then(({ data }) => commit('SET_COUNTER_PARTY_LIST', data))
  },
  fetchPaymentMethodTypes({ commit }) {
    dictionaryApi.getPaymentMethodTypes()
      .then(({ data }) => commit('SET_PAYMENT_METHOD_TYPES_LIST', data))
  },
  fetchDeliveryPaymentTypes({ commit }) {
    dictionaryApi.getDeliveryPaymentTypes()
      .then(({ data }) => commit('SET_DELIVERY_PAYMENT_TYPES_LIST', data))
  },
  fetchTransferKinds({ commit }) {
    dictionaryApi.getTransferKinds()
      .then(({ data }) => commit('SET_TRANSFER_KINDS_LIST', data))
  },
  fetchPaymentAmountScopes({ commit }) {
    dictionaryApi.getPaymentAmountScopes()
      .then(({ data }) => commit('SET_PAYMENT_AMOUNT_SCOPES_LIST', data))
  },
  fetchCashes({ commit }) {
    counterPartyApi.getCashes().then(({ data }) => commit('SET_CASH_LIST', data))
  },
  fetchTables({ commit }) {
    counterPartyApi.getTables().then(({ data }) => commit('SET_TABLE_LIST', data))
  },
  fetchRk7OrderTypes({ commit }) {
    orderTypeApi.getTypes().then(({ data }) => commit('SET_RK7_ORDER_TYPE_LIST', data))
  },
  fetchOrderCategories({ commit }) {
    orderTypeApi.getCategories().then(({ data }) => commit('SET_ORDER_CATEGORY_LIST', data))
  },
  fetchCurrencies({ commit }) {
    orderTypeApi.getCurrencies().then(({ data }) => commit('SET_CURRENCY_LIST', data))
  },
  fetchCrmClientGroups({ commit }) {
    systemSettingsApi.getCrmClientGroups().then(({ data }) => commit('SET_CRM_CLIENT_GROUPS', data))
  },
  fetchCrmContactTypes({ commit }) {
    systemSettingsApi.getCrmContactTypes().then(({ data }) => commit('SET_CRM_CONTACT_TYPES', data))
  },
  fetchRk7Reasons({ commit }) {
    paymentMethodApi.getReasons().then(({ data }) => commit('SET_RK7_REASONS_LIST', data))
  },
  fetchVacancyApplicationStatuses({ commit }) {
    dictionaryApi.getVacancyApplicationStatuses().then(({ data }) => commit('SET_VACANCY_APPLICATION_STATUS_LIST', data))
  },
  fetchCrmAccountTypes({ commit }) {
    systemSettingsApi.getCrmAccountTypes().then(({ data }) => commit('SET_CRM_ACCOUNT_TYPES_LIST', data))
  },
  fetchBranches({ commit }) {
    dictionaryApi.getBranches().then(({ data }) => commit('SET_BRANCHES_LIST', data))
  },
  fetchOrderStates({ commit }) {
    dictionaryApi.getOrderStates().then(({ data }) => commit('SET_ORDER_STATES_LIST', data))
  },
  fetchCustomDishTypes({ commit }) {
    dictionaryApi.getCustomDishTypes().then(({ data }) => commit('SET_CUSTOM_DISH_TYPES_LIST', data))
  },
}

const mutations = {
  SET_LANGUAGES(state, data) {
    state.languages = data
  },
  SET_APPLICATION_TYPES_LIST(state, data) {
    state.applicationTypesList = data
  },
  SET_ROLES_LIST(state, data) {
    state.rolesList = data
  },
  SET_CONTACT_TYPES_LIST(state, data) {
    state.contactTypesList = data
  },
  SET_CONTACTS_LIST(state, data) {
    state.contactsList = data
  },
  SET_APPLICATION_DESCRIPTION_TYPES_LIST(state, data) {
    state.applicationDescriptionTypesList = data
  },
  SET_VACANCY_TYPES_LIST(state, data) {
    state.vacancyTypesList = data
  },
  SET_SEX_LIST(state, data) {
    state.sexList = data
  },
  SET_CUSTOMER_DROPDOWN(state, items) {
    state.customerList = items
  },
  SET_CITY_DROPDOWN(state, items) {
    state.cityList = items
  },
  SET_PAYMENT_METHODS_LIST(state, items) {
    state.paymentMethodsList = items
  },
  SET_PAYMENT_METHOD_TYPES_LIST(state, items) {
    state.paymentMethodTypesList = items
  },
  SET_DELIVERY_PAYMENT_TYPES_LIST(state, items) {
    state.deliveryPaymentTypesList = items
  },
  SET_TRANSFER_KINDS_LIST(state, items) {
    state.transferKindsList = items
  },
  SET_PAYMENT_AMOUNT_SCOPES_LIST(state, items) {
    state.paymentAmountScopesList = items
  },
  SET_COUNTER_PARTY_LIST(state, items) {
    state.counterPartyTypeList = items
  },
  SET_CASH_LIST(state, items) {
    state.cashList = items
  },
  SET_TABLE_LIST(state, items) {
    state.tableList = items
  },
  SET_RK7_ORDER_TYPE_LIST(state, items) {
    state.rk7OrderTypeList = items
  },
  SET_ORDER_CATEGORY_LIST(state, items) {
    state.orderCategoryList = items
  },
  SET_CURRENCY_LIST(state, items) {
    state.currencies = items
  },
  SET_ORDER_TYPES_LIST(state, items) {
    state.orderTypesList = items
  },
  SET_CRM_CLIENT_GROUPS(state, items) {
    state.crmClientGroupsList = items
  },
  SET_CRM_CONTACT_TYPES(state, items) {
    state.crmContactTypesList = items
  },
  SET_RK7_REASONS_LIST(state, items) {
    state.rk7ReasonsList = items
  },
  SET_VACANCY_APPLICATION_STATUS_LIST(state, items) {
    state.vacancyApplicationStatusList = items
  },
  SET_CRM_ACCOUNT_TYPES_LIST(state, items) {
    state.crmAccountTypesList = items
  },
  SET_BRANCHES_LIST(state, items) {
    state.branchesList = items
  },
  SET_ORDER_STATES_LIST(state, items) {
    state.orderStatesList = items
  },
  SET_CUSTOM_DISH_TYPES_LIST(state, items) {
    state.customDishTypesList = items
  },
  ...generateFormHandlerMutations(state),
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
